import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowBack,
  Edit,
  Person,
  Email,
  Phone,
  CalendarToday,
  LocationOn,
  AttachMoney,
  Group,
  Comment,
  Payment,
  Receipt
} from '@mui/icons-material';
import { API_BASE_URL } from '../../config/api';
import Card from '../../components/ui/Card';
import Notification from '../../components/ui/Notification';
import StatusBadge from '../../components/ui/StatusBadge';

const BookingDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Fetch booking details
  useEffect(() => {
    const fetchBookingDetails = async () => {
      try {
        setLoading(true);
        const detailUrl = `${API_BASE_URL}/bookings/${id}/`;

        const response = await fetch(detailUrl, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch booking details');
        }

        const data = await response.json();
        setBooking(data.booking);
      } catch (error) {
        console.error('Error fetching booking details:', error);
        setNotification({
          type: 'error',
          message: 'Erreur lors du chargement des détails de la réservation',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchBookingDetails();
  }, [id]);

  // Handle status update
  const handleStatusUpdate = async (newStatus) => {
    try {
      setUpdatingStatus(true);
      const updateStatusUrl = `${API_BASE_URL}/bookings/${id}/status/`;

      const response = await fetch(updateStatusUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        throw new Error('Failed to update booking status');
      }

      const data = await response.json();

      // Update booking state with new status
      setBooking({
        ...booking,
        status: newStatus,
      });

      setNotification({
        type: 'success',
        message: 'Statut de la réservation mis à jour avec succès',
      });
    } catch (error) {
      console.error('Error updating booking status:', error);
      setNotification({
        type: 'error',
        message: 'Erreur lors de la mise à jour du statut de la réservation',
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Format datetime
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/bookings')}
            className="btn btn-outline flex items-center space-x-2"
          >
            <ArrowBack className="w-5 h-5" />
            <span>Retour</span>
          </button>

          <h1 className="text-2xl font-bold text-gray-900">
            Détails de la réservation
          </h1>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : booking ? (
        <div className="space-y-6">
          {/* Header with reference and status */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Référence: {booking.booking_reference}
                </h2>
                <p className="text-gray-500">
                  Créée le {formatDateTime(booking.created_at)}
                </p>
              </div>

              <div className="mt-4 md:mt-0 flex flex-col md:flex-row md:items-center space-y-2 md:space-y-0 md:space-x-4">
                <div className="flex items-center space-x-2">
                  <span className="text-gray-700">Statut:</span>
                  <StatusBadge status={booking.status} />
                </div>

                <div className="flex items-center space-x-2">
                  <select
                    value={booking.status}
                    onChange={(e) => handleStatusUpdate(e.target.value)}
                    className="input py-1 px-2 text-sm"
                    disabled={updatingStatus}
                  >
                    <option value="pending">En attente</option>
                    <option value="confirmed">Confirmée</option>
                    <option value="cancelled">Annulée</option>
                    <option value="completed">Terminée</option>
                  </select>

                  <button
                    onClick={() => handleStatusUpdate(booking.status)}
                    className="btn btn-sm btn-primary"
                    disabled={updatingStatus}
                  >
                    Mettre à jour
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Client Information */}
            <Card>
              <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <Person className="w-5 h-5 mr-2 text-blue-500" />
                Informations client
              </h2>

              <div className="space-y-4">
                <div className="flex items-start">
                  <Person className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Nom complet</p>
                    <p className="font-medium">{booking.first_name} {booking.last_name}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Email className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{booking.email}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Phone className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Téléphone</p>
                    <p className="font-medium">{booking.phone}</p>
                  </div>
                </div>

                {booking.special_requests && (
                  <div className="flex items-start">
                    <Comment className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                    <div className="w-full">
                      <p className="text-sm text-gray-500">Demandes spéciales</p>
                      <div className="mt-1 p-3 bg-gray-50 rounded-md border border-gray-100">
                        {booking.special_requests.split('\n').map((line, index) => (
                          <p key={index} className="mb-1 last:mb-0">
                            {line || <br />}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Booking Details */}
            <Card>
              <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <Receipt className="w-5 h-5 mr-2 text-blue-500" />
                Détails de la réservation
              </h2>

              <div className="space-y-4">
                <div className="flex items-start">
                  <LocationOn className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Type de réservation</p>
                    <p className="font-medium">
                      {booking.booking_type === 'destination' && 'Destination'}
                      {booking.booking_type === 'excursion' && 'Excursion'}
                      {booking.booking_type === 'package' && 'Forfait'}
                      {booking.booking_type === 'hotel' && 'Hôtel'}
                    </p>
                    <p className="text-sm text-gray-700">
                      {booking.booking_type === 'destination' && booking.destination_name}
                      {booking.booking_type === 'excursion' && booking.excursion_title}
                      {booking.booking_type === 'package' && booking.package_name}
                      {booking.booking_type === 'hotel' && booking.hotel_name}
                    </p>
                  </div>
                </div>

                {booking.end_date && (
                  <div className="flex items-start">
                    <CalendarToday className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Date de fin</p>
                      <p className="font-medium">{formatDate(booking.end_date)}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-start">
                  <CalendarToday className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Date de création</p>
                    <p className="font-medium">{formatDateTime(booking.created_at)}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Group className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Nombre de voyageurs</p>
                    <p className="font-medium">{booking.travelers}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <AttachMoney className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                  <div className="w-full">
                    <p className="text-sm text-gray-500">Prix total</p>
                    <p className="font-medium text-lg">{booking.total_price} €</p>

                    {booking.custom_price_enabled && (
                      <div className="mt-2 p-3 bg-blue-50 rounded-md border border-blue-100">
                        <div className="flex items-center mb-2">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                          <span className="text-sm font-medium text-blue-700">Prix personnalisé activé</span>
                        </div>
                        <div className="text-sm text-blue-600">
                          <p><strong>Prix unitaire personnalisé:</strong> {booking.custom_unit_price} €/personne</p>
                          <p><strong>Nombre de voyageurs:</strong> {booking.travelers}</p>
                          <p><strong>Calcul:</strong> {booking.custom_unit_price} € × {booking.travelers} = {booking.total_price} €</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Payment Information */}
          {(booking.payment_method || booking.payment_status || booking.payment_id) && (
            <Card>
              <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <Payment className="w-5 h-5 mr-2 text-blue-500" />
                Informations de paiement
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {booking.payment_method && (
                  <div className="flex items-start">
                    <div className="bg-blue-50 p-2 rounded-full mr-3">
                      <Payment className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Méthode de paiement</p>
                      <p className="font-medium">
                        {booking.payment_method === 'paypal' && 'PayPal'}
                        {booking.payment_method === 'credit_card' && 'Carte de crédit'}
                        {booking.payment_method === 'bank_transfer' && 'Virement bancaire'}
                        {booking.payment_method === 'standard' && 'Standard'}
                      </p>
                    </div>
                  </div>
                )}

                {booking.payment_status && (
                  <div className="flex items-start">
                    <div className="bg-blue-50 p-2 rounded-full mr-3">
                      <Receipt className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Statut du paiement</p>
                      <p className="font-medium">
                        {booking.payment_status === 'paid' && 'Payé'}
                        {booking.payment_status === 'pending' && 'En attente'}
                        {booking.payment_status === 'failed' && 'Échoué'}
                        {booking.payment_status === 'refunded' && 'Remboursé'}
                      </p>
                    </div>
                  </div>
                )}

                {booking.payment_id && (
                  <div className="flex items-start">
                    <div className="bg-blue-50 p-2 rounded-full mr-3">
                      <Receipt className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">ID de transaction</p>
                      <p className="font-medium">{booking.payment_id}</p>
                    </div>
                  </div>
                )}

                {booking.payment_date && (
                  <div className="flex items-start">
                    <div className="bg-blue-50 p-2 rounded-full mr-3">
                      <CalendarToday className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Date de paiement</p>
                      <p className="font-medium">{formatDateTime(booking.payment_date)}</p>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>
      ) : (
        <Card>
          <div className="text-center py-8">
            <p className="text-gray-500">Réservation non trouvée</p>
          </div>
        </Card>
      )}
    </div>
  );
};

export default BookingDetailPage;
